import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type APIKey } from '@/api/client'

export default function ApiKeys() {
  const qc = useQueryClient()
  const list = useQuery({ queryKey: ['api-keys'], queryFn: api.apiKeys.list })
  const [name, setName] = useState('')
  const [freshKey, setFreshKey] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: (n: string) => api.apiKeys.create(n),
    onSuccess: (res) => {
      setFreshKey(res.plain)
      setName('')
      qc.invalidateQueries({ queryKey: ['api-keys'] })
    },
  })
  const toggle = useMutation({
    mutationFn: (k: APIKey) => api.apiKeys.patch(k.id, !k.disabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  })
  const remove = useMutation({
    mutationFn: (id: number) => api.apiKeys.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  })

  return (
    <div className="space-y-4">
      <section className="border border-border rounded-lg p-4 bg-card text-card-foreground">
        <h2 className="text-sm font-semibold text-muted-foreground mb-3">Create API Key</h2>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) create.mutate(name.trim())
          }}
        >
          <input
            className="flex-1 border border-border rounded px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="e.g. shopping-saas-prod"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            type="submit"
            disabled={create.isPending || !name.trim()}
            className="bg-primary text-primary-foreground px-4 py-2 rounded disabled:opacity-50 hover:opacity-90 transition-opacity"
          >
            Create
          </button>
        </form>
        {freshKey && (
          <div className="mt-3 p-3 bg-warning-soft text-warning-soft-foreground border border-border rounded text-sm">
            <div className="font-semibold mb-1">
              Copy now — this key is shown only once.
            </div>
            <code className="block break-all font-mono text-xs">{freshKey}</code>
          </div>
        )}
      </section>

      <section className="border border-border rounded-lg p-4 bg-card text-card-foreground">
        <h2 className="text-sm font-semibold text-muted-foreground mb-3">Keys</h2>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-2">Name</th>
              <th>Prefix</th>
              <th>Status</th>
              <th>Last used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.data?.items.map((k) => (
              <tr key={k.id} className="border-t border-border">
                <td className="py-2">{k.name}</td>
                <td>
                  <code className="text-xs">{k.key_prefix}…</code>
                </td>
                <td>
                  {k.disabled ? (
                    <span className="text-destructive">disabled</span>
                  ) : (
                    <span className="text-success">active</span>
                  )}
                </td>
                <td className="text-xs text-muted-foreground">
                  {k.last_used_at ? new Date(k.last_used_at * 1000).toLocaleString() : '—'}
                </td>
                <td className="text-right space-x-2">
                  <button
                    onClick={() => toggle.mutate(k)}
                    className="text-xs px-2 py-1 border border-border rounded hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    {k.disabled ? 'Enable' : 'Disable'}
                  </button>
                  <button
                    onClick={() => confirm(`Delete "${k.name}"?`) && remove.mutate(k.id)}
                    className="text-xs px-2 py-1 border border-border rounded text-destructive hover:bg-destructive-soft transition-colors"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {list.data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="py-6 text-center text-muted-foreground">
                  No keys yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </section>
    </div>
  )
}
